import axios, { type InternalAxiosRequestConfig } from "axios";
import { getRefreshToken } from "@/console/apis/auth";

interface CustomInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
  __retryCount?: number;
}

const instance = axios.create({
  baseURL: import.meta.env.VITE_CONSOLE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  // biome-ignore lint/complexity/noForEach: <explanation>
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

instance.interceptors.response.use(
  async (response) => {
    if (
      response.config.url === "/api/auth/refresh_token" &&
      response.data.error === 10
    ) {
      window.location.href = "/login";
      return Promise.reject({
        response: {
          data: response.data,
        },
      });
    }

    if (response.config.url === "/api/auth/refresh_token") {
      return response;
    }

    if (response.data.error === 1) {
      return response;
    }

    if (response.data.status_code === 403) {
      const retryCount =
        (response.config as CustomInternalAxiosRequestConfig).__retryCount || 0;
      if (retryCount >= 1) {
        return Promise.reject({
          response: {
            data: response.data,
          },
        });
      }

      const accessToken = localStorage.getItem("token");
      const refreshToken = localStorage.getItem("refreshToken");

      if (accessToken && refreshToken) {
        const originalRequest =
          response.config as CustomInternalAxiosRequestConfig;

        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const { access_token, refresh_token } = await getRefreshToken(
              accessToken,
              refreshToken
            );

            localStorage.setItem("token", access_token);
            localStorage.setItem("refreshToken", refresh_token);

            isRefreshing = false;
            onRefreshed(access_token);

            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            originalRequest.__retryCount = retryCount + 1;
            return instance(originalRequest);
          } catch (error) {
            isRefreshing = false;
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            return Promise.reject(error);
          }
        } else {
          return new Promise((resolve) => {
            subscribeTokenRefresh((token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              originalRequest.__retryCount = retryCount + 1;
              resolve(instance(originalRequest));
            });
          });
        }
      }
    }

    return Promise.reject({
      response: {
        data: response.data,
      },
    });
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default instance;
